import { GradientedScroller, connected, withErrorBoundary, NoResults, Spinner } from 'shared'
import { absolutizePath } from 'shared/util'
import { TopLevelPage, ModelGrid } from 'ui'
import EventCard from 'ui/events/EventCard'
import ParkCard from 'ui/parks/ParkCard'
import { EventApi, AnnouncementApi, Park, ParkApi, Menu, MenuApi, User } from 'api'
import { addDays, format, startOfMonth, endOfMonth, isValid } from 'date-fns'
import { useEffect, useRef, useState } from 'react'
import Announcements from '../events/Announcements'
import FullCalendar from '@fullcalendar/react'
import dayGridPlugin from '@fullcalendar/daygrid'
import timeGridPlugin from '@fullcalendar/timegrid'
import adaptivePlugin from '@fullcalendar/adaptive'
import interactionPlugin from '@fullcalendar/interaction'

calendarGlobal = []
intervalId = null
EVENTS_PER_LOAD = 3

CalendarView = ({match}) ->
  feedItemId = match.params.id
  selectedPlacesPageSize = 8
  [calendar, setCalendar] = useState([
    {
      startDate: startOfMonth(new Date()),
      endDate: endOfMonth(new Date()),
      key: 'selection'
    }
  ])
  [events, setEvents] = useState([])
  [showReservations, setShowReservations] = useState(false)
  [selectedPlaces, setSelectedPlaces] = useState([])
  [selectedPlacesPage, setSelectedPlacesPage] = useState(1)
  [reservablePlaces, setReservablePlaces] = useState([])
  [selectedDate, setSelectedDate] = useState(new Date())
  [reservableDates, setReservableDates] = useState([])
  [allEventMenus, setAllEventMenus] = useState([])
  [selectedMenuId, setSelectedMenuId] = useState(null)
  [eventMenuIds, setEventMenuIds] = useState({})
  [menuIdsWithEvents, setMenuIdsWithEvents] = useState([])
  [loading, setLoading] = useState(true)
  [dateTotals, setDateTotals] = useState({})
  [dateLoadedCounts, setDateLoadedCounts] = useState({})
  [loadingMoreDates, setLoadingMoreDates] = useState({})
  selectedPlacesRequestRef = useRef(null)
  eventsRequestRef = useRef(0)
  lastLoadedRangeRef = useRef(null)
  selectedMenuIdRef = useRef(null)
  eventsRef = useRef([])
  announcements = AnnouncementApi.kick('index')

  parseDate = (dateTime) ->
    format(dateTime, "yyyy'-'MM'-'dd")

  dateKey = (value) ->
    String(value or '').slice(0, 10)

  dateParams = (calendar) ->
    {
      start_date: parseDate(calendar.startDate),
      end_date: parseDate(calendar.endDate),
      per_date_limit: EVENTS_PER_LOAD
    }

  rangeKey = (calendar) ->
    "#{parseDate(calendar.startDate)}:#{parseDate(calendar.endDate)}"

  isCountable = (model) ->
    return false if model.category == "multiday"
    return true if model.is_recurring
    model.category == "regular" or model.category == "family"

  countForDate = (list, key) ->
    count = 0
    for model in (list or [])
      continue unless isCountable(model)
      if model.is_recurring and model.occurrences?.length
        count += model.occurrences.filter((o) -> dateKey(o.date) == key).length
      else if dateKey(model.date) == key
        count += 1
    count

  mergeEvents = (existing, incoming) ->
    result = (existing or []).slice()
    indexById = {}
    result.forEach (event, i) -> indexById[String(event.id)] = i

    for event in (incoming or [])
      idx = indexById[String(event.id)]
      if idx?
        current = result[idx]
        if event.occurrences?.length or current.occurrences?.length
          seen = {}
          merged = []
          for occ in (current.occurrences or []).concat(event.occurrences or [])
            k = String(occ.id or "#{dateKey(occ.date)}-#{occ.time or ''}")
            unless seen[k]
              seen[k] = true
              merged.push(occ)
          current.occurrences = merged
      else
        indexById[String(event.id)] = result.length
        result.push(event)
    result

  applyFullCalendarAccessibilityFix = ->
    document.querySelectorAll('.fc-col-header-cell[role="columnheader"], .fc-col-header-cell[role="presentation"]').forEach (th) ->
      if not th.hasAttribute('scope')
        th.setAttribute('scope', 'col')

    document.querySelectorAll('th[role="presentation"]').forEach (th) ->
      if not th.hasAttribute('scope')
        th.setAttribute('scope', 'col')

    document.querySelectorAll('.fc-scrollgrid').forEach (table) ->
      cells = table.querySelectorAll('td, th')
      if cells.length <= 7
        table.removeAttribute('role')
        table.setAttribute('role', 'list')
        table.querySelectorAll('.fc-daygrid-day').forEach (cell) ->
          cell.setAttribute('role', 'listitem')
      else
        table.setAttribute('role', 'grid')

    document.querySelectorAll('.fc-daygrid-day-number').forEach (el) ->
      return if el.tagName.toLowerCase() is 'button'  # Skip if already converted

      dateLabel = el.getAttribute('aria-label') or el.textContent?.trim()
      button = document.createElement('button')
      button.className = el.className
      button.textContent = el.textContent?.trim() or ''
      button.setAttribute('type', 'button')
      button.setAttribute('tabindex', '0')
      button.setAttribute('aria-label', "Select date #{dateLabel or ''}")

      if el.id then button.id = el.id

      el.parentNode.replaceChild(button, el)

  hideCalendarLinks = ->
    document.querySelectorAll('.fc-daygrid-day-number').forEach (el) ->
      if el.tagName.toLowerCase() is 'a'
        el.style.display = 'none'

  useEffect(() ->
    setTimeout(applyFullCalendarAccessibilityFix, 1500)
    setTimeout(hideCalendarLinks, 50)
    reserveDate = sessionStorage.getItem('calendar-reserve-date')
    if reserveDate
      sessionStorage.removeItem('calendar-reserve-date')
      loadPlacesForDate(reserveDate)
    undefined
  , [])

  useEffect(() ->
    MenuApi.load().then (allMenus) ->
      eventMenus = (allMenus or []).filter (menu) -> menu.item_type == 'Event' and menu.valid
      setAllEventMenus(eventMenus)
    undefined
  , [])

  fetchReservableDates = (startDate = null, endDate = null) ->
    start = startDate or new Date()
    end = endDate or endOfMonth(new Date())

    ParkApi.availableDates({
      start_date: format(start, "yyyy-MM-dd"),
      end_date: format(end, "yyyy-MM-dd")
    }).then (dates) ->
      setReservableDates(dates.map((date) -> { date }))

  handleCalendarEventClick = (info) ->
    return if loading
    info.jsEvent?.preventDefault()
    info.jsEvent?.stopPropagation()

    if info.event?.extendedProps?.isLoadMore
      loadMoreForDate(dateKey(info.event.extendedProps.dateKey or info.event.startStr))
      return

    if info.event?.title?.includes("Open to Reserve")
      application.go('/community-calendar') if feedItemId
      loadPlacesForDate(new Date(info.event.start))
      return

    targetUid = info.event.extendedProps?.targetUid
    application.go("/community-calendar/#{targetUid}") if targetUid

  loadPlacesForDate = (date) ->
    normalizedDate = if typeof date == 'string'
      new Date("#{date}T00:00:00")
    else
      new Date(date.getFullYear(), date.getMonth(), date.getDate())
    formattedDate = format(normalizedDate, "yyyy-MM-dd")
    setSelectedDate(normalizedDate)
    setSelectedPlaces([])
    setSelectedPlacesPage(1)

    # Ignore stale responses when user clicks dates quickly.
    requestKey = "#{formattedDate}-#{Date.now()}"
    selectedPlacesRequestRef.current = requestKey

    ParkApi.openAvailable({ start_date: formattedDate, end_date: formattedDate }).then (data) ->
      return unless selectedPlacesRequestRef.current == requestKey
      setSelectedPlaces(data?.places or [])

  loadData = (calendar = null) ->
    calendar = {
      startDate: startOfMonth(new Date()),
      endDate: endOfMonth(new Date())
    } unless calendar

    key = rangeKey(calendar)
    requestId = ++eventsRequestRef.current
    lastLoadedRangeRef.current = key
    setLoading(true)
    setDateTotals({})
    setDateLoadedCounts({})
    setLoadingMoreDates({})

    params = dateParams(calendar)
    params.menu_id = selectedMenuIdRef.current if selectedMenuIdRef.current
    EventApi.filtered(params).then (data) ->
      return unless requestId == eventsRequestRef.current
      activeMenuId = selectedMenuIdRef.current
      menuIds = data.menuIdsWithEvents or []
      nextEvents = data.events or []
      nextTotals = data.dateTotals or {}
      nextLoaded = {}
      for k, total of nextTotals
        nextLoaded[k] = Math.min(Number(total) or 0, EVENTS_PER_LOAD)

      eventsRef.current = nextEvents
      setEvents(nextEvents)
      setDateTotals(nextTotals)
      setDateLoadedCounts(nextLoaded)
      if data.eventMenuIds and Object.keys(data.eventMenuIds).length > 0
        setEventMenuIds(data.eventMenuIds)
      if menuIds.length > 0
        setMenuIdsWithEvents(menuIds)
        if activeMenuId and not menuIds.map(String).includes(String(activeMenuId))
          selectedMenuIdRef.current = null
          setSelectedMenuId(null)
      setLoading(false)
    , ->
      return unless requestId == eventsRequestRef.current
      eventsRef.current = []
      setEvents([])
      setEventMenuIds({})
      setMenuIdsWithEvents([])
      setDateTotals({})
      setDateLoadedCounts({})
      setLoading(false)

    fetchReservableDates(calendar.startDate, calendar.endDate)

  loadMoreForDate = (key) ->
    return unless key
    return if loading or loadingMoreDates[key]
    total = Number(dateTotals[key] or 0)
    counted = countForDate(eventsRef.current, key)
    loaded = Math.max(Number(dateLoadedCounts[key] or 0), counted)
    if loaded != Number(dateLoadedCounts[key] or 0)
      setDateLoadedCounts (prev) ->
        next = Object.assign({}, prev)
        next[key] = if total > 0 then Math.min(loaded, total) else loaded
        next
    remaining = Math.max(0, total - loaded)
    return if remaining <= 0

    setLoadingMoreDates (prev) ->
      next = Object.assign({}, prev)
      next[key] = true
      next
    params = { start_date: key, end_date: key, per_date_limit: remaining, offset: loaded }
    params.menu_id = selectedMenuIdRef.current if selectedMenuIdRef.current
    requestId = eventsRequestRef.current

    EventApi.filtered(params).then (data) ->
      return unless requestId == eventsRequestRef.current
      incoming = data.events or []
      merged = mergeEvents(eventsRef.current, incoming)
      nextLoaded = total

      eventsRef.current = merged
      setEvents(merged.slice())
      setDateLoadedCounts (prev) ->
        next = Object.assign({}, prev)
        next[key] = nextLoaded
        next
      if data.dateTotals and Object.keys(data.dateTotals).length > 0
        setDateTotals (prev) -> Object.assign({}, prev, data.dateTotals)
      setLoadingMoreDates (prev) ->
        next = Object.assign({}, prev)
        delete next[key]
        next
    , ->
      setLoadingMoreDates (prev) ->
        next = Object.assign({}, prev)
        delete next[key]
        next

  selectMenu = (menuId) ->
    return if loading
    selectedMenuIdRef.current = menuId
    setSelectedMenuId(menuId)
    loadData(calendar[0])

  resetFilters = ->
    return if loading
    selectedMenuIdRef.current = null
    setSelectedMenuId(null)
    setSelectedPlaces([])
    setSelectedPlacesPage(1)
    loadData(calendar[0])

  updateDateRange = (range) ->
    setSelectedDate(range.selection.startDate)
    setCalendar([range.selection])

  printCalendar = ->
    nav = document.querySelector('.MainNav')
    footer = document.querySelector('footer')

    nav?.style.setProperty('display', 'none', 'important')
    footer?.style.setProperty('display', 'none', 'important')

    restore = ->
      nav?.style.removeProperty('display')
      footer?.style.removeProperty('display')
      window.removeEventListener('afterprint', restore)

    window.addEventListener('afterprint', restore)

    window.print()

  useEffect ->
    handleKeyDown = (e) ->
      if (e.ctrlKey or e.metaKey) and e.key.toLowerCase() is 'p'
        e.preventDefault()
        printCalendar()
    window.addEventListener('keydown', handleKeyDown)
    -> window.removeEventListener('keydown', handleKeyDown)
  , []

  # Load the full displayed month, not FullCalendar's visible grid (which includes padding days).
  handleDatesSet = (info) ->
    monthStart = startOfMonth(info.view.currentStart)
    monthEnd = endOfMonth(info.view.currentStart)
    newRange =
      startDate: monthStart
      endDate: monthEnd
      key: 'selection'
    key = rangeKey(newRange)
    return if lastLoadedRangeRef.current == key
    setCalendar([newRange])
    setSelectedDate(monthStart)
    setSelectedPlaces([])
    loadData(newRange)

  activeMenuIdSet = menuIdsWithEvents.map(String)
  menus = allEventMenus.filter (menu) -> activeMenuIdSet.includes(String(menu.id))

  selectedDateString = if selectedDate instanceof Date then format(selectedDate, "yyyy-MM-dd") else null
  selectedPlacesTotalPages = Math.max(1, Math.ceil(selectedPlaces.length / selectedPlacesPageSize))
  selectedPlacesCurrentPage = Math.min(selectedPlacesPage, selectedPlacesTotalPages)
  selectedPlacesStart = (selectedPlacesCurrentPage - 1) * selectedPlacesPageSize
  paginatedSelectedPlaces = selectedPlaces.slice(selectedPlacesStart, selectedPlacesStart + selectedPlacesPageSize)

  # API already filters by menu_id when selected.
  filteredEvents = events

  calendarEvents = filteredEvents.flatMap((model) ->
    base = "/app#/community-calendar"
    if model.category == "multiday"
      startDate = new Date("#{model.date}T09:00:00")  # Default start time at 9 AM
      endDate = new Date("#{model.end_date}T18:00:00")  # End time at 6 PM
      return [] unless isValid(startDate) and isValid(endDate)

      return {
        id: model.id
        targetUid:  model.targetUid
        title: model.name
        start: startDate.toISOString()
        end: endDate.toISOString()
        url: "#{absolutizePath(base)}/#{model.targetUid}" 
        allDay: true
        dateKey: dateKey(model.date)
        skipLoadMoreCount: true
        sortIndex: 0
      }

    else if model.is_recurring and model.occurrences?  # Handle recurring events
      return model.occurrences.map((occurrence) ->
        time = occurrence.time
        return null unless time
        closeTime = occurrence.close_time or time
        occurrenceDate = new Date("#{occurrence.date}T#{time}:00")
        endTime = new Date("#{occurrence.date}T#{closeTime}:00")
        return null unless isValid(occurrenceDate)
        endTime = new Date(occurrenceDate.getTime() + 60 * 60 * 1000) unless isValid(endTime)

        return {
          id: occurrence.id
          targetUid:  model.targetUid
          title: model.name
          start: occurrenceDate.toISOString()
          end: endTime.toISOString()
          allDay: false
          url: "#{absolutizePath(base)}/#{model.targetUid}"
          dateKey: dateKey(occurrence.date)
          skipLoadMoreCount: false
          sortIndex: 0
        }
      ).filter(Boolean)

    else  # Regular / family / alert / offer
      openTime = model.open_time_local or '09:00'
      closeTime = model.close_time_local or openTime
      startDate = new Date("#{model.date}T#{openTime}:00")
      endDate = new Date("#{model.date}T#{closeTime}:00")
      return null unless model.date and isValid(startDate)
      endDate = new Date(startDate.getTime() + 60 * 60 * 1000) unless isValid(endDate)

      return {
        id: model.id
        targetUid:  model.targetUid
        title: model.name
        start: startDate.toISOString()
        end: endDate.toISOString()
        url: "#{absolutizePath(base)}/#{model.targetUid}"
        allDay: false
        dateKey: dateKey(model.date)
        skipLoadMoreCount: not (model.category == "regular" or model.category == "family")
        sortIndex: 0
      }
  ).filter(Boolean)

  eventsByDate = {}
  for event in calendarEvents
    continue unless event.dateKey
    (eventsByDate[event.dateKey] ||= []).push(event)

  visibleEvents = []
  for key, dayEvents of eventsByDate
    allDayEvents = dayEvents.filter((e) -> e.allDay)
    timedEvents = dayEvents.filter((e) -> not e.allDay)
    countable = timedEvents.filter((e) -> not e.skipLoadMoreCount)
    timedExtras = timedEvents.filter((e) -> e.skipLoadMoreCount)
    loaded = Number(dateLoadedCounts[key] or 0)
    limit = if loaded > 0 then loaded else EVENTS_PER_LOAD
    shownCountable = countable.slice(0, limit)
    extraBudget = Math.max(0, limit - shownCountable.length)
    shownTimed = shownCountable.concat(timedExtras.slice(0, extraBudget))
    visibleEvents.push.apply(visibleEvents, allDayEvents.concat(shownTimed))
    total = Number(dateTotals[key] or 0)
    remaining = Math.max(0, total - Math.max(loaded, shownCountable.length))
    if remaining > 0
      visibleEvents.push({
        id: "load-more-#{key}"
        title: if loadingMoreDates[key] then "Loading..." else "Load more (#{remaining})"
        start: "#{key}T23:59:00"
        allDay: false
        display: 'list-item'
        classNames: ["calendar-load-more-event"]
        isLoadMore: true
        dateKey: key
        sortIndex: 1
      })

  calendarEvents = visibleEvents

  calendarEvents.push(
    ...reservableDates.map((entry) ->
      isSelectedReserveDate = selectedDateString == entry.date and selectedPlaces.length > 0
      {
        id: "reserve-#{entry.date}"
        title: "● Open to Reserve"
        start: entry.date
        allDay: true
        sortIndex: 0
        classNames: if isSelectedReserveDate then ["selected-open-to-reserve-event"] else []
      }
    )
  )

  <TopLevelPage className="Calendar" modelId={feedItemId} base="/community-calendar" returnUrl="/community-calendar">
    <h1
      className="visually-hidden"
    >
      Community Calendar
    </h1>
    <div className={"row calendar-section justify-content-center mt-3" + (if loading then " is-loading" else "")}>
      <div className="col position-relative">
        {loading &&
          <div className="calendar-loading-overlay">
            <Spinner text="Loading data..." />
          </div>
        }
        <FullCalendar
          schedulerLicenseKey="0456860429-fcs-1763595698"
          plugins={[ dayGridPlugin, timeGridPlugin, adaptivePlugin, interactionPlugin]}
          initialView="dayGridMonth"
          fixedWeekCount={false}
          timeZone='local'
          customButtons={{
            printButton: {
              text: 'Print',
              click: -> printCalendar() unless loading
            }
          }}
          headerToolbar={{
            left: 'title',
            right: 'printButton today prev,next'
          }}
          datesSet={handleDatesSet}
          dateClick={(info) -> setSelectedDate(new Date(info.dateStr)) unless loading}
          eventClick={handleCalendarEventClick}
          eventOrder="sortIndex,start,-duration,allDay,title"
          events={calendarEvents}
          height="auto"
        />
      </div>
    </div>

    {selectedPlaces.length > 0 &&
      <section>
        <div className="row justify-content-center">
          <div className="col-md-8">
            <h1 className="section-heading-lowercase my-3">Available Amenity Reservations</h1>
          </div>
        </div>

        <div className="row">
          {selectedPlaces.map((place, index) ->
            <div key={index} className="col-sm-6 col-lg-4 col-xl-3">
              <ParkCard
                park={place}
                base="/community-calendar"
                selectedDate={if selectedDate instanceof Date then format(selectedDate, "yyyy-MM-dd") else format(new Date(), "yyyy-MM-dd")}
              />
            </div>
          )}
        </div>
      </section>
    }
  </TopLevelPage>

export default withErrorBoundary connected CalendarView